import { type FC, type MouseEventHandler, type ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import { trackEvent } from 'dibs-tracking';
import classnames from 'classnames';
import { useHomePageContext } from '../HomePageContext.tsx';
import { useBuyerIncentivesHomepageList } from 'dibs-buyer-incentives/exports/useBuyerIncentivesList.ts';
import BuyerIncentiveIcon from 'dibs-buyer-incentives/exports/BuyerIncentiveIcon.ts';
import BuyerIncentiveCopy from 'dibs-buyer-incentives/exports/BuyerIncentiveCopy.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import {
    useIsRemoveShopByTestVariant,
    trackRemoveShopByTest,
} from '../helpers/removeShopByTestHelpers.ts';

import dibsCss from 'dibs-css';
import styles from './HpSharedSkinnyBannerModule.module.css';
import { Button } from 'dibs-elements/exports/Button.tsx';

const sharedCopy = {
    learnMore: (
        <FormattedMessage id="HpSharedSkinnyBannerModule.learnMore" defaultMessage="Learn More" />
    ),
};

const copyByUserType = {
    buyer: {
        eyebrow: (
            <FormattedMessage
                id="HpSharedSkinnyBannerModule.buyer.subtitle"
                defaultMessage="1stDibs Purchase Protection"
            />
        ),
        title: (isMobile: boolean): ReactNode => (
            <FormattedMessage
                id="HpSharedSkinnyBannerModule.buyer.title"
                defaultMessage="Shop with confidence. <italic>We’ve got you.</italic>"
                values={{
                    italic: chunks => (
                        <span
                            className={classnames(
                                isMobile
                                    ? [
                                          styles.reducedLineHeight,
                                          dibsCss.sassyFontHeaderLargeItalic,
                                          dibsCss.block,
                                      ]
                                    : dibsCss.sassyFontHeaderXxxLargeItalic
                            )}
                        >
                            {chunks}
                        </span>
                    ),
                }}
            />
        ),
        subtitle: null,
    },
    trade: {
        eyebrow: null,
        title: (isMobile: boolean): ReactNode => (
            <FormattedMessage
                id="HpSharedSkinnyBannerModule.trade.title"
                defaultMessage="1stDibs Purchase <block>Protection</block>"
                values={{
                    block: chunks => (
                        <span className={isMobile ? dibsCss.block : ''}>{chunks}</span>
                    ),
                }}
            />
        ),
        subtitle: (
            <FormattedMessage
                id="HpSharedSkinnyBannerModule.trade.subtitle"
                defaultMessage="Your creative vision, with our protection."
            />
        ),
    },
};

const HpSharedSkinnyBannerModule: FC = () => {
    const { isMobile, isTrade } = useHomePageContext();
    const intl = useIntl();
    const buyerIncentivesList = useBuyerIncentivesHomepageList(isTrade);
    const isRemoveShopByTestVariant = useIsRemoveShopByTestVariant();

    const copy = copyByUserType[isTrade ? 'trade' : 'buyer'];

    const handleClick: MouseEventHandler = useCallback(event => {
        trackEvent(
            {
                category: 'promo interaction',
                action: 'Learn More Click',
                label: '[homepage]',
            },
            event
        );
    }, []);

    return (
        <>
            <div
                className={classnames(
                    dibsCss.bgCyan100,
                    dibsCss.roundedXsmall,
                    dibsCss.textNoir,
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter,
                    isMobile
                        ? [dibsCss.pMedium, dibsCss.mtLarge]
                        : [dibsCss.ptLarge, dibsCss.pbLarger, dibsCss.mtLarger],
                    {
                        [dibsCss.mbLarge]: isRemoveShopByTestVariant && isMobile,
                        [dibsCss.mbLarger]: isRemoveShopByTestVariant && !isMobile,
                    }
                )}
            >
                <div className={dibsCss.textCenter}>
                    {copy.eyebrow && (
                        <div
                            className={
                                isMobile
                                    ? dibsCss.sassyFontBodySizeMediumTypeNormal
                                    : dibsCss.sassyFontBodySizeLargeTypeHeavy
                            }
                        >
                            {copy.eyebrow}
                        </div>
                    )}
                    <div
                        className={classnames(
                            styles.reducedLineHeight,
                            isMobile
                                ? dibsCss.sassyFontHeaderSizeLarge
                                : dibsCss.sassyFontHeaderSizeXxxLarge
                        )}
                    >
                        {typeof copy.title === 'function' ? copy.title(isMobile) : copy.title}
                    </div>
                    {copy.subtitle && (
                        <div
                            className={
                                isMobile
                                    ? dibsCss.sassyFontBody
                                    : dibsCss.sassyFontBodySizeLargeTypeHeavy
                            }
                        >
                            {copy.subtitle}
                        </div>
                    )}
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.flexRow,
                            isMobile
                                ? [
                                      dibsCss.flexCol,
                                      dibsCss.gapYXsmall,
                                      dibsCss.mySmall,
                                      dibsCss.pxSmall,
                                  ]
                                : [dibsCss.myMedium, dibsCss.selfStretch, dibsCss.wFull]
                        )}
                    >
                        {buyerIncentivesList.map((name, index) => (
                            <div
                                key={index}
                                className={classnames(
                                    dibsCss.flex,
                                    isMobile
                                        ? [dibsCss.flexRow]
                                        : [
                                              dibsCss.flexCol,
                                              dibsCss.flex1,
                                              dibsCss.minW0,
                                              dibsCss.pxMedium,
                                              dibsCss.borderBlue300,
                                          ],
                                    {
                                        [dibsCss.borderL]: !isMobile && index !== 0,
                                        [dibsCss.borderLSolid]: !isMobile && index !== 0,
                                        [dibsCss.sassyFontBodyLarge]: !isMobile,
                                        [dibsCss.gapXsmall]: !isMobile,
                                        [dibsCss.sassyFontBody]: isMobile,
                                        [dibsCss.gapSmaller]: isMobile,
                                        [dibsCss.itemsCenter]: isMobile,
                                    }
                                )}
                            >
                                <span
                                    className={classnames(dibsCss.selfCenter, dibsCss.fillBlue600)}
                                >
                                    <BuyerIncentiveIcon name={name} size={isMobile ? '24' : '40'} />
                                </span>

                                <span
                                    className={classnames(
                                        styles.reducedLineHeight,
                                        isMobile
                                            ? [dibsCss.sassyFontBody, dibsCss.textLeft]
                                            : dibsCss.sassyFontBodySizeLargeTypeHeavy
                                    )}
                                >
                                    <BuyerIncentiveCopy name={name} />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {isMobile ? (
                    <SeoLink
                        className={classnames(
                            dibsCss.sassyFontBody,
                            dibsCss.inlineBlock,
                            dibsCss.textNoir
                        )}
                        dataTn="skinny-banner-link"
                        linkData={{
                            path: handleLocaleUrl('/about/promise/', intl.locale),
                            isLinkable: true,
                        }}
                        onClick={handleClick}
                        target="_blank"
                    >
                        {sharedCopy.learnMore}
                    </SeoLink>
                ) : (
                    <Button
                        className={styles.button}
                        style="circle"
                        type="primary"
                        size="large"
                        dataTn="skinny-banner-button"
                        onClick={handleClick}
                        target="_blank"
                        href={'/about/promise/'}
                        customColors={{
                            background: 'sassyColorBlue600',
                            hoverBackground: 'sassyColorBlue700',
                        }}
                    >
                        {sharedCopy.learnMore}
                    </Button>
                )}
            </div>
            <div className={dibsCss.absolute}>
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            // We want to track Remove Shop By test only when user scrolls down
                            // to the bottom of the banner where Shop By module supposed to be.
                            trackRemoveShopByTest();
                        }
                    }}
                />
            </div>
        </>
    );
};

export default HpSharedSkinnyBannerModule;
